import React, { useState, useEffect } from 'react';
import PayPalCheckout from '../components/PayPalCheckout';

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || '/api');

const readApiResponse = async (res, fallback = 'Erreur serveur') => {
  const text = await res.text();
  let data = {};

  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`${fallback} (HTTP ${res.status}) : réponse serveur non JSON`);
    }
  } else if (!res.ok) {
    throw new Error(`${fallback} (HTTP ${res.status})`);
  }

  if (!res.ok || data.success === false) {
    throw new Error(data.error || data.message || `${fallback} (HTTP ${res.status})`);
  }

  return data;
};

export default function XtreamRenewal() {
  const token = window.location.pathname.split('/renew/')[1];

  const [linkInfo, setLinkInfo] = useState(null);
  const [linkError, setLinkError] = useState('');
  const [loadingLink, setLoadingLink] = useState(true);

  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('momo');
  const [aggregatorsConfig, setAggregatorsConfig] = useState(null);
  const [countriesCatalog, setCountriesCatalog] = useState([]);
  const [selectedAggregator, setSelectedAggregator] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [renewalResult, setRenewalResult] = useState(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState('');

  // Agrégateurs Mobile Money proposables (config admin).
  useEffect(() => {
    fetch(`${API_URL}/payments/aggregators`)
      .then(r => r.json())
      .then(data => {
        setAggregatorsConfig(data.aggregators || null);
        setCountriesCatalog(data.countries || []);
      })
      .catch(() => { /* non bloquant */ });
  }, []);

  useEffect(() => {
    if (!token) { setLinkError('Lien invalide'); setLoadingLink(false); return; }
    fetch(`${API_URL}/payments/xtream-renewal/${token}`)
      .then(r => readApiResponse(r, 'Impossible de charger le lien'))
      .then(data => {
        setLinkInfo(data);
        if (Array.isArray(data.plans) && data.plans.length > 0) {
          setSelectedPlanKey(data.plan_key || data.plans[0].key);
        }
      })
      .catch((err) => setLinkError(err.message || 'Impossible de charger le lien'))
      .finally(() => setLoadingLink(false));
  }, [token]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const returnedFromPayment = query.get('payment') === 'success';
    const paymentToken = query.get('payment_token') || query.get('token') || localStorage.getItem('xtream_renewal_payment_token') || token;
    if (!returnedFromPayment || !paymentToken) return undefined;

    let cancelled = false;
    let timer = null;

    const pollPayment = async () => {
      try {
        setPaymentStatus((current) => current === 'idle' ? 'checking' : current);
        const res = await fetch(`${API_URL}/payments/status/${paymentToken}`);
        const data = await readApiResponse(res, 'Erreur vérification paiement');
        if (cancelled) return;

        if (data.status === 'paid') {
          setRenewalResult(data);
          setPaymentStatus('success');
          localStorage.removeItem('xtream_renewal_payment_token');
        } else if (data.status === 'pending' || data.status === 'processing') {
          setPaymentStatus('pending');
          timer = setTimeout(pollPayment, 4000);
        } else {
          setPaymentStatus('failed');
          setPayError(data.error || 'Paiement non confirmé');
        }
      } catch {
        if (!cancelled) {
          setPaymentStatus('pending');
          timer = setTimeout(pollPayment, 5000);
        }
      }
    };

    pollPayment();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token]);

  const AGG_LABELS = {
    moneyfusion: { label: 'MoneyFusion', icon: '🟢' },
    leekpay:     { label: 'LeekPay',     icon: '🔵' },
  };

  // Pays déduit du téléphone s'il porte un indicatif international (+221…).
  const phoneClean = String(telephone || '').replace(/[\s-]/g, '');
  const customerCountry = phoneClean.startsWith('+')
    ? (countriesCatalog.find(c => phoneClean.startsWith(c.dialCode)) || {}).code || null
    : null;

  const availableAggregators = (() => {
    if (!aggregatorsConfig) return ['moneyfusion'];
    const enabled = ['moneyfusion', 'leekpay'].filter(n => aggregatorsConfig[n]?.enabled);
    if (!customerCountry) return enabled;
    const covering = enabled.filter(n => (aggregatorsConfig[n]?.countries || []).includes(customerCountry));
    return covering.length ? covering : enabled;
  })();

  const activeAggregator = availableAggregators.includes(selectedAggregator)
    ? selectedAggregator
    : (availableAggregators[0] || null);
  const momoUnavailable = paymentMethod === 'momo' && availableAggregators.length === 0;

  const handlePay = async (e) => {
    e.preventDefault();
    setPayError('');
    if (!activeAggregator) {
      setPayError('Le paiement Mobile Money est momentanément indisponible. Essayez PayPal / Carte.');
      return;
    }
    setPaying(true);
    try {
      const res = await fetch(`${API_URL}/payments/xtream-renewal/${token}/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom, email, telephone, plan_key: selectedPlanKey, aggregator: activeAggregator, ...(customerCountry && { country: customerCountry }) })
      });
      const data = await readApiResponse(res, 'Erreur lors du paiement');
      if (data.success && data.paymentUrl) {
        if (data.token) localStorage.setItem('xtream_renewal_payment_token', data.token);
        window.location.href = data.paymentUrl;
      } else {
        setPayError(data.error || 'Erreur lors du paiement');
      }
    } catch (err) {
      setPayError(err.message || 'Erreur de connexion');
    } finally {
      setPaying(false);
    }
  };

  const validateForm = () => {
    if (!nom || !email || !telephone) {
      setPayError('Veuillez remplir tous les champs');
      return false;
    }
    setPayError('');
    return true;
  };

  const createPaypalOrder = async () => {
    if (!validateForm()) throw new Error('Veuillez remplir tous les champs');
    const res = await fetch(`${API_URL}/payments/xtream-renewal/${token}/paypal-create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nom, email, telephone, plan_key: selectedPlanKey })
    });
    const data = await readApiResponse(res, 'Erreur PayPal');
    return data.orderId;
  };

  const capturePaypalOrder = async (orderId) => {
    const res = await fetch(`${API_URL}/payments/xtream-renewal/${token}/paypal-capture`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orderId })
    });
    return readApiResponse(res, 'Confirmation PayPal impossible');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
  const plans = Array.isArray(linkInfo?.plans) ? linkInfo.plans : [];
  const selectedPlan = plans.find(plan => plan.key === selectedPlanKey) || null;
  const renewalDays = selectedPlan ? selectedPlan.days : linkInfo?.days;
  const renewalAmount = selectedPlan ? selectedPlan.amount : linkInfo?.amount;
  const renewalName = selectedPlan ? selectedPlan.name : linkInfo?.plan_name;

  return (
    <div style={s.bg}>
      <div style={s.card}>
        {/* Logo */}
        <div style={s.logo}>
          <span style={s.logoText}>FLUXIVO</span>
        </div>

        {loadingLink ? (
          <div style={s.center}>⏳ Chargement...</div>
        ) : linkError ? (
          <div style={s.errorBox}>
            <div style={s.errorIcon}>❌</div>
            <div style={s.errorTitle}>Lien invalide</div>
            <div style={s.errorDesc}>{linkError}</div>
          </div>
        ) : paymentStatus === 'success' ? (
          <div style={s.successBox}>
            ✅ Renouvellement confirmé ! Votre compte a été prolongé.
            {renewalResult?.expiry_date && (
              <div style={{ marginTop: '8px', color: '#E2E8F0', fontSize: '0.9rem' }}>
                Nouvelle expiration : {formatDate(renewalResult.expiry_date)}
              </div>
            )}
            {renewalResult?.xtream?.xtreamUsername && (
              <div style={{ marginTop: '10px', color: '#CBD5E1', fontSize: '0.86rem' }}>
                Identifiant : <strong>{renewalResult.xtream.xtreamUsername}</strong>
              </div>
            )}
            {(renewalResult?.receiptDownloadUrl || renewalResult?.receiptUrl) && (
              <a
                href={renewalResult.receiptDownloadUrl || renewalResult.receiptUrl}
                style={s.receiptBtn}
              >
                Télécharger le reçu
              </a>
            )}
          </div>
        ) : paymentStatus === 'checking' || paymentStatus === 'pending' ? (
          <div style={s.center}>⏳ Vérification du paiement...</div>
        ) : (
          <>
            {/* Récapitulatif */}
            <div style={s.summary}>
              <div style={s.summaryTitle}>Renouvellement d'abonnement</div>
              <div style={s.summaryGrid}>
                <div style={s.summaryItem}>
                  <span style={s.summaryKey}>Compte</span>
                  <span style={s.summaryVal}>{linkInfo.username}</span>
                </div>
                <div style={s.summaryItem}>
                  <span style={s.summaryKey}>Durée</span>
                  <span style={{ ...s.summaryVal, color: '#22C55E' }}>{renewalDays} jours</span>
                </div>
                {plans.length > 0 && (
                  <div style={{ ...s.summaryItem, alignItems: 'stretch', flexDirection: 'column', gap: '8px' }}>
                    <span style={s.summaryKey}>Plan</span>
                    <div style={s.planGrid}>
                      {plans.map(plan => (
                        <button
                          key={plan.key}
                          type="button"
                          style={{ ...s.planBtn, ...(selectedPlanKey === plan.key ? s.planBtnActive : {}) }}
                          onClick={() => setSelectedPlanKey(plan.key)}
                        >
                          <span style={s.planName}>{plan.name}</span>
                          <span style={s.planMeta}>{plan.days}j · {plan.amount.toLocaleString('fr-FR')} FCFA</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={s.summaryItem}>
                  <span style={s.summaryKey}>Expiration actuelle</span>
                  <span style={s.summaryVal}>{formatDate(linkInfo.current_expiry)}</span>
                </div>
                <div style={s.summaryItem}>
                  <span style={s.summaryKey}>Montant</span>
                  <span style={{ ...s.summaryVal, color: '#22C55E', fontSize: '1.2rem', fontWeight: '700' }}>
                    {(renewalAmount || 0).toLocaleString('fr-FR')} FCFA
                    <span style={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: '400', marginLeft: '8px' }}>
                      (≈ {((renewalAmount || 0) / 600).toFixed(2)} $)
                    </span>
                  </span>
                </div>
                {renewalName && plans.length === 0 && (
                  <div style={s.summaryItem}>
                    <span style={s.summaryKey}>Plan</span>
                    <span style={s.summaryVal}>{renewalName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Champs communs */}
            <div style={s.form}>
              <div style={s.formTitle}>Vos informations</div>

              <div style={s.field}>
                <label style={s.label}>Nom complet</label>
                <input style={s.input} type="text" placeholder="Jean Dupont" required
                  value={nom} onChange={e => setNom(e.target.value)} />
              </div>

              <div style={s.field}>
                <label style={s.label}>Adresse email</label>
                <input style={s.input} type="email" placeholder="jean@email.com" required
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>

              <div style={s.field}>
                <label style={s.label}>Numéro de téléphone</label>
                <input style={s.input} type="tel" placeholder="07 XX XX XX XX" required
                  value={telephone} onChange={e => setTelephone(e.target.value)} />
              </div>

              {payError && <div style={s.payError}>❌ {payError}</div>}

              {/* Toggle méthode de paiement */}
              <div style={s.toggleRow}>
                <button type="button"
                  style={{ ...s.toggleBtn, ...(paymentMethod === 'momo' ? s.toggleActive : {}) }}
                  onClick={() => { setPaymentMethod('momo'); setPayError(''); }}>
                  📱 Mobile Money
                </button>
                <button type="button"
                  style={{ ...s.toggleBtn, ...(paymentMethod === 'paypal' ? s.toggleActive : {}) }}
                  onClick={() => { setPaymentMethod('paypal'); setPayError(''); }}>
                  PayPal / Carte
                </button>
              </div>

              {/* Section Mobile Money (MoneyFusion / LeekPay) */}
              {paymentMethod === 'momo' && (
                momoUnavailable ? (
                  <div style={s.payError}>
                    Le paiement Mobile Money n'est pas disponible actuellement. Choisissez « PayPal / Carte ».
                  </div>
                ) : (
                  <form onSubmit={handlePay}>
                    {availableAggregators.length > 1 && (
                      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                        {availableAggregators.map(name => (
                          <button key={name} type="button"
                            style={{ ...s.toggleBtn, flex: '1 1 140px', ...(activeAggregator === name ? s.toggleActive : {}) }}
                            onClick={() => setSelectedAggregator(name)}>
                            {AGG_LABELS[name]?.icon} {AGG_LABELS[name]?.label || name}
                          </button>
                        ))}
                      </div>
                    )}
                    <button type="submit" style={s.payBtn} disabled={paying}>
                      {paying ? '⏳ Redirection...' : 'Procéder au paiement'}
                    </button>
                    <p style={s.secure}>🔒 Paiement sécurisé via {AGG_LABELS[activeAggregator]?.label || 'Mobile Money'}</p>
                  </form>
                )
              )}

              {paymentMethod === 'paypal' && (
                <div>
                  <PayPalCheckout key={selectedPlanKey} createOrder={createPaypalOrder}
                    captureOrder={capturePaypalOrder}
                    onSuccess={result => { setRenewalResult(result); setPaymentStatus('success'); }}
                    onError={err => setPayError(err.message || 'Paiement PayPal impossible')} />
                  <p style={s.secure}>Paiement sécurisé via PayPal</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  bg: {
    minHeight: '100vh',
    background: '#06090F',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Inter', -apple-system, sans-serif"
  },
  card: {
    background: '#111827',
    border: '1px solid #1E2D3D',
    borderRadius: '20px',
    padding: '36px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
  },
  logo: { textAlign: 'center', marginBottom: '28px' },
  logoText: {
    fontSize: '1.6rem',
    fontWeight: '900',
    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '0.1em'
  },
  center: { textAlign: 'center', color: '#94A3B8', padding: '40px 0' },
  errorBox: { textAlign: 'center', padding: '20px 0' },
  errorIcon: { fontSize: '2.5rem', marginBottom: '12px' },
  errorTitle: { color: '#F1F5F9', fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' },
  errorDesc: { color: '#64748B', fontSize: '0.9rem' },

  summary: {
    background: '#0D1117',
    border: '1px solid #1E2D3D',
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '24px'
  },
  summaryTitle: { color: '#94A3B8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px', fontWeight: '600' },
  summaryGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  summaryItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summaryKey: { color: '#64748B', fontSize: '0.85rem' },
  summaryVal: { color: '#E2E8F0', fontSize: '0.9rem', fontWeight: '500' },
  planGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '8px' },
  planBtn: {
    background: '#111827',
    border: '1px solid #2D3F50',
    borderRadius: '10px',
    padding: '10px 12px',
    color: '#CBD5E1',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  planBtnActive: { borderColor: '#22C55E', background: '#16A34A22' },
  planName: { fontSize: '0.88rem', fontWeight: '700', color: '#F1F5F9' },
  planMeta: { fontSize: '0.78rem', color: '#94A3B8' },

  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  formTitle: { color: '#F1F5F9', fontWeight: '600', fontSize: '0.95rem', marginBottom: '4px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { color: '#94A3B8', fontSize: '0.8rem', fontWeight: '500' },
  input: {
    background: '#0D1117',
    border: '1px solid #2D3F50',
    borderRadius: '10px',
    padding: '12px 14px',
    color: '#F1F5F9',
    fontSize: '0.95rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  },
  payError: {
    background: '#EF444420',
    border: '1px solid #EF444455',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#F87171',
    fontSize: '0.85rem'
  },
  payBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #16A34A, #15803D)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontWeight: '700',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '4px',
    boxShadow: '0 4px 20px rgba(22,163,74,0.3)',
    transition: 'opacity 0.2s'
  },
  secure: { textAlign: 'center', color: '#4B5563', fontSize: '0.78rem', marginTop: '16px', marginBottom: 0 },
  toggleRow: { display: 'flex', gap: '8px', marginTop: '4px' },
  toggleBtn: {
    flex: 1, padding: '10px', border: '2px solid #2D3F50', borderRadius: '10px',
    background: '#0D1117', color: '#94A3B8', cursor: 'pointer', fontWeight: '600',
    fontSize: '0.85rem', transition: 'all 0.2s'
  },
  toggleActive: { borderColor: '#22C55E', background: '#16A34A', color: '#fff' },
  successBox: {
    background: '#16A34A20', border: '1px solid #22C55E55', borderRadius: '10px',
    padding: '14px', color: '#22C55E', fontWeight: '600', textAlign: 'center', fontSize: '0.9rem'
  },
  receiptBtn: {
    display: 'block',
    marginTop: '16px',
    padding: '12px 14px',
    borderRadius: '10px',
    background: '#16A34A',
    color: '#fff',
    textDecoration: 'none',
    fontWeight: '700',
    textAlign: 'center'
  }
};
